import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-02cb2cac"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "bp-full-exp" }
const _hoisted_2 = { class: "bp-full-exp__header" }
const _hoisted_3 = {
  class: "bp-full-exp__profile",
  "aria-hidden": "true"
}
const _hoisted_4 = ["src"]
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "bp-full-exp__title" }, "Full Explanation", -1))
const _hoisted_6 = { class: "bp-full-exp__body" }
const _hoisted_7 = {
  key: 0,
  class: "bp-full-exp__loading"
}
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = ["disabled"]

import { computed, ref, watch } from "vue";
import LingoModal from "@/modules/main/components/manage/LingoModalComponent.vue";
import defaultAvatar from "@/assets/avatar.svg";

interface TeacherBotLike {
  id?: string;
  name?: string;
  avatar?: string | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BlockedPracticeFullExplanationModal',
  props: {
    modelValue: { type: Boolean },
    loading: { type: Boolean, default: false },
    content: { default: "" },
    fallbackContent: { default: "" },
    teacherBot: { default: null },
    allowDismiss: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "continue"],
  setup(__props: any, { emit }) {

const props = __props;





const scrollContainer = ref<HTMLElement | null>(null);
const hasReachedBottom = ref(false);
const isScrollable = ref(false);

const internalValue = computed({
  get() {
    return props.modelValue;
  },
  set(next: boolean) {
    emit("update:modelValue", next);
  }
});

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function applyInlineFormatting(value: string): string {
  let output = escapeHtml(value);
  output = output.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  output = output.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  return output;
}

function normalizeMarkdown(value: string): string {
  return value.replace(/\r\n/g, "\n").replace(/\r/g, "\n").replace(/\\n/g, "\n");
}

function renderMarkdownSubset(markdown: string): string {
  const lines = normalizeMarkdown(markdown).split("\n");
  const blocks: string[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let blockquoteLines: string[] = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    const paragraph = paragraphLines.map(line => applyInlineFormatting(line)).join("<br/>");
    blocks.push(`<p>${paragraph}</p>`);
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listItems.length || !listType) return;
    const items = listItems.map(item => `<li>${applyInlineFormatting(item)}</li>`).join("");
    blocks.push(`<${listType}>${items}</${listType}>`);
    listItems = [];
    listType = null;
  };

  const flushBlockquote = () => {
    if (!blockquoteLines.length) return;
    const linesHtml = blockquoteLines.map(line => `<p>${applyInlineFormatting(line)}</p>`).join("");
    blocks.push(`<blockquote>${linesHtml}</blockquote>`);
    blockquoteLines = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line.length) {
      flushParagraph();
      flushList();
      flushBlockquote();
      continue;
    }

    if (/^---+$/.test(line)) {
      flushParagraph();
      flushList();
      flushBlockquote();
      blocks.push("<hr/>");
      continue;
    }

    const headingMatch = line.match(/^#{1,6}\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      flushBlockquote();
      blocks.push(`<h3>${applyInlineFormatting(headingMatch[1])}</h3>`);
      continue;
    }

    const quoteMatch = line.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph();
      flushList();
      blockquoteLines.push(quoteMatch[1]);
      continue;
    }

    const orderedMatch = line.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) {
      flushParagraph();
      flushBlockquote();
      if (listType && listType !== "ol") {
        flushList();
      }
      listType = "ol";
      listItems.push(orderedMatch[1]);
      continue;
    }

    const bulletMatch = line.match(/^[-*+]\s+(.*)$/);
    if (bulletMatch) {
      flushParagraph();
      flushBlockquote();
      if (listType && listType !== "ul") {
        flushList();
      }
      listType = "ul";
      listItems.push(bulletMatch[1]);
      continue;
    }

    flushList();
    flushBlockquote();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();
  flushBlockquote();

  return blocks.join("");
}

const renderedContent = computed(() => {
  const primary = typeof props.content === "string" ? props.content.trim() : "";
  if (primary.length) {
    return primary;
  }

  const fallback = typeof props.fallbackContent === "string" ? props.fallbackContent.trim() : "";
  if (fallback.length) {
    return fallback;
  }

  return "No full explanation is available yet. Continue learning to proceed.";
});

const renderedContentHtml = computed(() => {
  return renderMarkdownSubset(renderedContent.value);
});

const headerAvatarSrc = computed(() => {
  if (props.teacherBot && typeof props.teacherBot.avatar === "string" && props.teacherBot.avatar.trim()) {
    return props.teacherBot.avatar.trim();
  }
  return defaultAvatar;
});

const canContinue = computed(() => {
  if (props.loading) {
    return false;
  }
  if (!isScrollable.value) {
    return true;
  }
  return hasReachedBottom.value;
});

function recalculateScrollability() {
  const container = scrollContainer.value;
  if (!container) {
    isScrollable.value = false;
    hasReachedBottom.value = true;
    return;
  }

  const canScroll = container.scrollHeight - container.clientHeight > 4;
  isScrollable.value = canScroll;
  if (!canScroll) {
    hasReachedBottom.value = true;
    return;
  }

  hasReachedBottom.value = container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
}

function handleContentScroll() {
  const container = scrollContainer.value;
  if (!container) {
    return;
  }
  hasReachedBottom.value = container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
}

function handleContinue() {
  if (!canContinue.value) {
    return;
  }
  emit("continue");
}

watch(
  () => props.modelValue,
  isOpen => {
    if (!isOpen) {
      hasReachedBottom.value = false;
      isScrollable.value = false;
      return;
    }

    setTimeout(() => {
      recalculateScrollability();
    }, 0);
  },
  { immediate: true }
);

watch(renderedContentHtml, () => {
  if (!props.modelValue) {
    return;
  }
  setTimeout(() => {
    recalculateScrollability();
  }, 0);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(LingoModal, {
      modelValue: internalValue.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((internalValue).value = $event)),
      "show-close-button": _ctx.allowDismiss,
      "close-on-overlay": _ctx.allowDismiss,
      "close-on-esc": _ctx.allowDismiss,
      centered: true,
      size: "640"
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("img", {
              class: "bp-full-exp__profile-image",
              src: headerAvatarSrc.value,
              alt: ""
            }, null, 8, _hoisted_4)
          ]),
          _hoisted_5
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("button", {
          class: "bp-full-exp__continue",
          disabled: !canContinue.value,
          onClick: handleContinue
        }, " Continue Learning ", 8, _hoisted_9)
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_6, [
          (_ctx.loading)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, "Loading explanation..."))
            : (_openBlock(), _createElementBlock("div", {
                key: 1,
                ref_key: "scrollContainer",
                ref: scrollContainer,
                class: "bp-full-exp__content",
                innerHTML: renderedContentHtml.value,
                onScroll: handleContentScroll
              }, null, 40, _hoisted_8))
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "show-close-button", "close-on-overlay", "close-on-esc"])
  ]))
}
}

})